import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useShots from '../../contexts/shots/provider';
import { setBackstageCanvasVolume } from '../../stores/actions/meeting';
import { GreenroomState as GS } from '../../stores/types/types';
import Icon, { COLORS, ICONS } from '../general-components/icon';
import ScrubBar from '../general-components/scrub-bar';
import InfoBadge, { InfoBadgeMsg } from '../general-components/tooltips/info-badge';

export default function AudioMixer() {
	const dispatch = useDispatch();
	const host = useSelector((s: GS) => s.SessionReducer.host);
	const backstageCanvasVolume = useSelector((s: GS) => s.MeetingReducer.backstageCanvasVolume);

	const [audioMixerValue, setAudioMixerValue] = useState<number>(1);
	const [audioMixerValueColor, setAudioMixerValueColor] = useState<string>('text-gray');
	const [audioMixerValueIcon, setAudioMixerValueIcon] = useState<string>(ICONS.VOLUME_3);
	const [audioMixerValueIconColor,
		setAudioMixerValueIconColor] = useState<string>(COLORS.TEXT_LIGHT_GRAY);
	const [audioMixerLocked, setAudioMixerLocked] = useState<boolean>(true);

	const mixerChangeDebounce = useRef<NodeJS.Timeout | null>(null);

	const { isOnCanvas } = useShots();

	const updateAudioMixerUI = (newValue: number) => {
		setAudioMixerValue(newValue);
		if (Math.round(newValue * 100) === 0) {
			setAudioMixerValueIcon(ICONS.VOLUME_MUTE);
		} else if (newValue < 0.33) {
			setAudioMixerValueIcon(ICONS.VOLUME_1);
		} else if (newValue < 0.66) {
			setAudioMixerValueIcon(ICONS.VOLUME_2);
		} else {
			setAudioMixerValueIcon(ICONS.VOLUME_3);
		}
	};
	const eventOnAudioMixerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const audioMixerValueChange = Number((event.target as any).value);
		if (audioMixerValueChange === 0 || audioMixerValueChange) {
			updateAudioMixerUI(audioMixerValueChange);
			if (mixerChangeDebounce.current) { clearTimeout(mixerChangeDebounce.current); }
			mixerChangeDebounce.current = setTimeout(() => {
				if (process.env.REACT_APP_NODE_ENV === 'dev') {
					// eslint-disable-next-line no-console
					console.log('[BDEV-5171] canvas volume slider -> dispatch setBackstageCanvasVolume', {
						value: audioMixerValueChange, at: new Date().toISOString(),
					});
				}
				dispatch(setBackstageCanvasVolume(audioMixerValueChange));
			}, 100);
		}
	};

	useEffect(() => {
		if (backstageCanvasVolume !== audioMixerValue) {
			updateAudioMixerUI(backstageCanvasVolume);
		}
	}, [backstageCanvasVolume]);

	useEffect(() => {
		if (!host) {
			setAudioMixerValueColor('text-green');
			setAudioMixerValueIconColor(COLORS.PALE_GREEN);
			setAudioMixerLocked(true);
		} else {
			setAudioMixerValueColor('text-gray');
			setAudioMixerValueIconColor(COLORS.TEXT_LIGHT_GRAY);
			setAudioMixerLocked(false);
		}

		if (isOnCanvas) {
			setAudioMixerValueColor('text-dark-gray');
			setAudioMixerValueIconColor(COLORS.DARK_GRAY);
			setAudioMixerLocked(true);
		}
	}, [host, isOnCanvas]);

	return (
		<div className='audio-mixer-wrapper align-items-center padding-small padding-left-medium padding-right-medium background-light border-radius-medium-small width-full'>
			<span className={classNames('margin-right-extra-small', { 'text-dark-gray': isOnCanvas })}>Canvas volume</span>
			<InfoBadge below message={host ? InfoBadgeMsg.HostCanvasVolume : InfoBadgeMsg.CanvasVolume}/>
			<ScrubBar wrapperClassNames={['margin-right-small margin-left-xsmall', { locked: audioMixerLocked }]} onChange={eventOnAudioMixerChange} value={audioMixerValue} disabled={audioMixerLocked} />
			<Icon className='margin-right-extra-small' name={audioMixerValueIcon} color={audioMixerValueIconColor} size={20} />
			<span className={classNames('audio-mixer-value', audioMixerValueColor)}>{Math.round((audioMixerValue) * 100)}%</span>
		</div>
	);
}
